import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { KeyConfig, ListenMode, Panel, TalkMode } from '@bccomms/shared';
import { chTarget, userTarget } from '@bccomms/shared';
import { session } from '../../lib/session';
import { useStore } from '../../store';
import { Field, SwatchPicker, Toggle } from '../ui';
import { usePanels } from './usePanels';

const newKey = (): KeyConfig => ({ id: crypto.randomUUID(), label: null, targets: [], talk: 'both', listen: 'toggle', listenDefault: true, color: null, vox: false });

export function PanelsTab() {
  const [panels, refresh] = usePanels();
  const channels = useStore((s) => s.channels);
  const users = useStore((s) => s.users);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Panel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!selectedId && panels[0]) setSelectedId(panels[0].id);
  }, [panels, selectedId]);
  useEffect(() => {
    const p = panels.find((x) => x.id === selectedId);
    setDraft(p ? structuredClone(p) : null);
    setSaved(false);
  }, [selectedId, panels]);

  const assigned = users.filter((u) => u.panelId === selectedId).length;

  const create = () => {
    const p: Panel = { id: '', name: 'New panel', keys: [newKey()] };
    setSelectedId(null);
    setDraft(p);
  };
  const save = async () => {
    if (!draft) return;
    setError(null);
    try {
      const keys = draft.keys.filter((k) => k.targets.length);
      if (!keys.length) throw new Error('add at least one key with a target');
      const res = (await session.admin('admin.panels.upsert', { panel: { ...draft, id: draft.id || undefined, keys } })) as Panel;
      await refresh();
      setSelectedId(res.id);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  const remove = async () => {
    if (!draft?.id || !confirm(`Delete panel ${draft.name}? ${assigned} user(s) will lose their keys.`)) return;
    await session.admin('admin.panels.delete', { id: draft.id });
    setSelectedId(null);
    await refresh();
  };

  const patchKey = (i: number, patch: Partial<KeyConfig>) => {
    if (!draft) return;
    const keys = draft.keys.map((k, j) => (j === i ? { ...k, ...patch } : k));
    setDraft({ ...draft, keys });
    setSaved(false);
  };
  const move = (i: number, dir: -1 | 1) => {
    if (!draft) return;
    const keys = [...draft.keys];
    const j = i + dir;
    if (j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j], keys[i]];
    setDraft({ ...draft, keys });
  };
  const toggleTarget = (i: number, t: string) => {
    const k = draft?.keys[i];
    if (!k) return;
    patchKey(i, { targets: k.targets.includes(t) ? k.targets.filter((x) => x !== t) : [...k.targets, t] });
  };

  return (
    <>
      <div className="admin__title">
        <h2>Panels</h2>
        <div className="row">
          <select className="select" style={{ width: 220 }} value={selectedId ?? ''} onChange={(e) => setSelectedId(e.target.value || null)}>
            {!selectedId && <option value="">— unsaved —</option>}
            {panels.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button className="btn btn--neutral" onClick={create}>
            <Plus size={16} /> New
          </button>
        </div>
      </div>

      {!draft ? (
        <p className="muted">No panels yet. Create one to define key layouts.</p>
      ) : (
        <>
          <div className="row row--wrap" style={{ marginBottom: 16 }}>
            <Field label="Panel name">
              <input className="input" style={{ width: 260 }} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <span className="muted" style={{ paddingTop: 8 }}>
              {draft.id ? `assigned to ${assigned} user(s)` : 'not saved yet'}
            </span>
          </div>

          {draft.keys.map((k, i) => (
            <div key={k.id} className="keyedit">
              <div className="keyedit__n">
                <div>{i + 1}</div>
                <button className="iconbtn" style={{ width: 28, height: 28 }} aria-label="Move up" onClick={() => move(i, -1)}>
                  <ArrowUp size={14} />
                </button>
                <button className="iconbtn" style={{ width: 28, height: 28 }} aria-label="Move down" onClick={() => move(i, 1)}>
                  <ArrowDown size={14} />
                </button>
              </div>
              <div>
                <Field label="Targets (multiple = group key)">
                  <div className="checks">
                    {channels.map((c) => (
                      <button key={c.id} type="button" className={`check ${k.targets.includes(chTarget(c.id)) ? 'check--on' : ''}`} onClick={() => toggleTarget(i, chTarget(c.id))}>
                        {c.short}
                        {c.kind === 'ifb' ? ' · IFB' : ''}
                      </button>
                    ))}
                    {users.map((u) => (
                      <button key={u.id} type="button" className={`check ${k.targets.includes(userTarget(u.id)) ? 'check--on' : ''}`} onClick={() => toggleTarget(i, userTarget(u.id))}>
                        @{u.displayName}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Label override">
                  <input className="input" placeholder="auto" maxLength={24} value={k.label ?? ''} onChange={(e) => patchKey(i, { label: e.target.value || null })} />
                </Field>
              </div>
              <div>
                <Field label="Talk">
                  <select className="select" value={k.talk} onChange={(e) => patchKey(i, { talk: e.target.value as TalkMode })}>
                    <option value="momentary">Momentary (push-to-talk)</option>
                    <option value="latch">Latching</option>
                    <option value="both">Both (hold = PTT, tap = latch)</option>
                    <option value="none">No talk</option>
                  </select>
                </Field>
                <Field label="Listen">
                  <select className="select" value={k.listen} onChange={(e) => patchKey(i, { listen: e.target.value as ListenMode })}>
                    <option value="toggle">Toggle</option>
                    <option value="always">Always on (locked)</option>
                    <option value="none">No listen (talk only)</option>
                  </select>
                </Field>
                {k.listen === 'toggle' && <Toggle label="Listen on by default" on={k.listenDefault} onChange={(v) => patchKey(i, { listenDefault: v })} />}
                {k.talk !== 'none' && (
                  <Toggle
                    label="VOX (auto-talk on voice)"
                    help="Talks automatically when the user's mic crosses their VOX threshold — no PTT needed. Layers under manual PTT/latch on this key."
                    on={k.vox}
                    onChange={(v) => patchKey(i, { vox: v })}
                  />
                )}
              </div>
              <div>
                <Field label="Colour">
                  <SwatchPicker allowNull value={k.color} onChange={(c) => patchKey(i, { color: c })} />
                </Field>
                <button className="btn btn--danger btn--sm" onClick={() => setDraft({ ...draft, keys: draft.keys.filter((_, j) => j !== i) })}>
                  <Trash2 size={14} /> Remove key
                </button>
              </div>
            </div>
          ))}

          <div className="row row--between" style={{ marginTop: 16 }}>
            <button className="btn btn--neutral" onClick={() => setDraft({ ...draft, keys: [...draft.keys, newKey()] })}>
              <Plus size={16} /> Add key
            </button>
            <div className="row">
              {error && <span className="error">{error}</span>}
              {saved && <span className="chip chip--success">Saved</span>}
              {draft.id && (
                <button className="btn btn--danger" onClick={() => void remove()}>
                  <Trash2 size={16} /> Delete panel
                </button>
              )}
              <button className="btn btn--primary" onClick={() => void save()}>
                <Save size={16} /> Save panel
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
